import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailLayout } from '@/components/ProfileMenu/DetailLayout';
import { useUpdateUsername } from '@/components/ProfileMenu/hooks/useUpdateUsername';
import {
  ErrorMessage,
  getResponseErrorCode,
  logRequestError,
} from '@/utils/errors';
import { submitOnEnter } from '@/utils/form';
import { isValidUsername } from '@/utils/validation';

const USERNAME_ALREADY_EXISTS_CODE = 'UsernameAlreadyExists';

enum FieldError {
  Invalid = 'invalid',
  AlreadyExists = 'alreadyExists',
}

interface Props {
  onBack: () => void;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
}

export const UsernameView: React.FC<Props> = ({
  onBack,
  onClose,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [error, setError] = useState<FieldError | null>(null);

  const isEmpty = username.length === 0;

  const { updateUsername, isPending } = useUpdateUsername();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);

    if (error) {
      setError(null);
    }
  };

  const handleSave = async () => {
    if (isPending || isEmpty) {
      return;
    }

    if (!isValidUsername(username)) {
      setError(FieldError.Invalid);
      return;
    }

    setError(null);

    try {
      await updateUsername(username);
      onSuccess();
    } catch (requestError) {
      if (getResponseErrorCode(requestError) === USERNAME_ALREADY_EXISTS_CODE) {
        setError(FieldError.AlreadyExists);
        return;
      }

      logRequestError(ErrorMessage.ChangeUsername, requestError);
      onError();
    }
  };

  return (
    <DetailLayout
      title={t('changeUsername.title')}
      isSaveDisabled={isEmpty}
      isSaving={isPending}
      onBack={onBack}
      onClose={onClose}
      onSave={handleSave}
    >
      <p className="mt-4 text-body-14 text-muted-foreground md:mt-0">
        {t('changeUsername.message')}
      </p>
      <label className="mt-6 mb-2 text-body-14 text-foreground md:text-body-16">
        {t('changeUsername.label')}
      </label>
      <input
        type="text"
        autoComplete="off"
        maxLength={36}
        value={username}
        onChange={handleChange}
        onKeyDown={submitOnEnter(handleSave)}
        placeholder={t('changeUsername.placeholder')}
        className="h-14 rounded-xl border border-transparent bg-on-primary-surface px-4 text-body-16 text-foreground outline-none placeholder:text-subtle-foreground md:h-12"
      />
      {error === FieldError.Invalid && (
        <p className="mt-2 text-body-14 text-destructive">
          {t('changeUsername.validationError')}
        </p>
      )}
      {error === FieldError.AlreadyExists && (
        <p className="mt-2 text-body-14 text-destructive">
          {t('changeUsername.alreadyExistsError')}
        </p>
      )}
    </DetailLayout>
  );
};
